import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ICollaborateur,
  ICollaborateurMetadataRequest,
  ICollaborateurPage,
  ICvMetadata
} from 'src/app/shared/models/collaborateur';

export interface ICollaborateurSearchParams {
  q?: string;
  active?: boolean;
  page?: number;
  size?: number;
  sort?: string;
}

@Injectable({ providedIn: 'root' })
export class CollaborateurService {

  private readonly apiUrl = '/api/v1/collaborateurs';

  constructor(private http: HttpClient) {}

  // ── Liste paginée / filtrée (CDC §8.2) ──────────────────────────────────────

  search(params: ICollaborateurSearchParams): Observable<ICollaborateurPage> {
    const httpParams: { [key: string]: string } = {
      page: String(params.page ?? 0),
      size: String(params.size ?? 20),
      sort: params.sort ?? 'nom,asc'
    };
    if (params.q) {
      httpParams['q'] = params.q;
    }
    if (params.active !== undefined && params.active !== null) {
      httpParams['active'] = String(params.active);
    }
    return this.http.get<ICollaborateurPage>(this.apiUrl, { params: httpParams });
  }

  getById(id: string): Observable<ICollaborateur> {
    return this.http.get<ICollaborateur>(`${this.apiUrl}/${id}`);
  }

  // ── Création / modification (multipart : metadata + cvFile) ────────────────

  create(metadata: ICollaborateurMetadataRequest, cvFile: File): Observable<ICollaborateur> {
    return this.http.post<ICollaborateur>(this.apiUrl, this.buildFormData(metadata, cvFile));
  }

  update(id: string, metadata: ICollaborateurMetadataRequest, cvFile: File | null): Observable<ICollaborateur> {
    return this.http.put<ICollaborateur>(`${this.apiUrl}/${id}`, this.buildFormData(metadata, cvFile));
  }

  // ── Activation / suppression ────────────────────────────────────────────────

  changeActivation(id: string, active: boolean): Observable<ICollaborateur> {
    return this.http.patch<ICollaborateur>(`${this.apiUrl}/${id}/activation`, null, {
      params: { active: String(active) }
    });
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  // ── CV ───────────────────────────────────────────────────────────────────

  getCvMetadata(id: string): Observable<ICvMetadata> {
    return this.http.get<ICvMetadata>(`${this.apiUrl}/${id}/cv`);
  }

  /** URL directe à utiliser dans un lien de téléchargement / nouvel onglet */
  downloadCvUrl(id: string): string {
    return `${this.apiUrl}/${id}/cv/download`;
  }

  /** Récupère les octets du CV pour un affichage inline (visualisateur) */
  getCvBlob(id: string): Observable<Blob> {
    return this.http.get(this.downloadCvUrl(id), { responseType: 'blob' });
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private buildFormData(metadata: ICollaborateurMetadataRequest, cvFile: File | null): FormData {
    const formData = new FormData();
    formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    if (cvFile) {
      formData.append('cvFile', cvFile, cvFile.name);
    }
    return formData;
  }
}
